'use client'

import { FC, useContext, useMemo } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { cn } from '@/lib/utils'
import type { ChatSettings } from '@/types'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  IconBolt,
  IconBrain,
  IconBrandAsana,
  IconStars,
  IconCheck
} from '@tabler/icons-react'

// For gpt-5-mini, use 'minimal' instead of 'none'
type ReasoningEffort = Extract<
  NonNullable<ChatSettings['reasoningEffort']>,
  'minimal' | 'low' | 'medium' | 'high'
>

interface Option {
  key: ReasoningEffort
  label: string
  description: string
  icon: React.ReactNode
}

export const PublicReasoningToggle: FC = () => {
  const { chatSettings, setChatSettings } = useContext(ChatbotUIChatContext)
  const { t } = useTranslation()

  // Default to 'minimal' for gpt-5-mini compatibility
  const value = chatSettings?.reasoningEffort
  const effectiveValue: ReasoningEffort =
    value === 'low' || value === 'medium' || value === 'high' || value === 'minimal'
      ? value
      : 'minimal'

  const options: Option[] = useMemo(
    () => [
      {
        key: 'minimal',
        label: t('Auto'),
        description: t('Quick responses'),
        icon: <IconBolt size={20} className="text-muted-foreground" />
      },
      {
        key: 'low',
        label: t('Expert'),
        description: t('Thinks hard'),
        icon: <IconBrain size={20} className="text-muted-foreground" />
      },
      {
        key: 'medium',
        label: t('Heavy'),
        description: t('Team of Experts'),
        icon: <IconBrandAsana size={20} className="text-muted-foreground" />
      },
      {
        key: 'high',
        label: t('Max'),
        description: t('Genius-level Experts'),
        icon: <IconStars size={20} className="text-muted-foreground" />
      }
    ],
    [t]
  )

  const handleChange = (next: ReasoningEffort) => {
    if (!chatSettings) return

    setChatSettings({
      ...chatSettings,
      reasoningEffort: next
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-lg border border-slate-200/30 p-2 hover:bg-slate-200 dark:border-slate-700/20 dark:hover:bg-slate-800"
          aria-label={t('Reasoning level')}
        >
          <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
            {options.find((o) => o.key === effectiveValue)?.icon}
            <span>{options.find((o) => o.key === effectiveValue)?.label}</span>
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" sideOffset={5} className="min-w-[220px]">
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          {t('Reasoning Effort')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {options.map((opt) => {
          const selected = effectiveValue === opt.key
          return (
            <DropdownMenuItem
              key={opt.key}
              className={cn(
                'flex items-center justify-between py-2',
                selected && 'bg-blue-50 dark:bg-blue-900/20'
              )}
              onClick={() => handleChange(opt.key)}
            >
              <div className="flex items-center gap-2">
                <div className="shrink-0">{opt.icon}</div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {opt.label}
                  </span>
                  <span className="text-muted-foreground text-xs">{opt.description}</span>
                </div>
              </div>
              <div className="ml-3 flex items-center">
                {selected ? (
                  <IconCheck size={16} className="text-primary" />
                ) : (
                  <div className="size-4" />
                )}
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
